import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FolderKanban, Bell, ShieldCheck, Settings, LogOut, Search as SearchIcon, Sun, Moon, UserCog, BookOpen } from "lucide-react";
import { BauenLogo } from "@/components/BauenLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const ADMIN_LINKS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clientes", icon: Users, testid: "nav-clients" },
  { to: "/projects", label: "Projetos", icon: FolderKanban, testid: "nav-projects" },
  { to: "/users", label: "Administradores", icon: UserCog, testid: "nav-users" },
  { to: "/notifications", label: "Notificações", icon: Bell, testid: "nav-notifications" },
  { to: "/audit", label: "Auditoria", icon: ShieldCheck, testid: "nav-audit" },
  { to: "/settings", label: "Configurações", icon: Settings, testid: "nav-settings" },
];
const CLIENT_LINKS = [
  { to: "/", label: "Meu Painel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/projects", label: "Meus Projetos", icon: FolderKanban, testid: "nav-projects" },
  { to: "/notifications", label: "Notificações", icon: Bell, testid: "nav-notifications" },
  { to: "/guide", label: "Guia de uso", icon: BookOpen, testid: "nav-guide" },
];
const CLIENT_OWNER_LINK = { to: "/client-users", label: "Usuários", icon: UserCog, testid: "nav-client-users" };

export function SidebarContent({ onSearchOpen, onNavigate }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const links = user?.role === "ADMIN"
    ? ADMIN_LINKS
    : (user?.client_role === "DONO" ? [...CLIENT_LINKS, CLIENT_OWNER_LINK] : CLIENT_LINKS);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const r = await api.get("/notifications", { params: { unread_only: true } });
        setUnread(r.data.length);
      } catch (_e) { void _e; }
    };
    load();
    timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const handleClick = () => { if (onNavigate) onNavigate(); };

  return (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b">
        <BauenLogo />
      </div>
      <div className="px-3 py-3">
        <button
          onClick={() => { onSearchOpen?.(); handleClick(); }}
          data-testid="sidebar-search-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
        >
          <SearchIcon size={16} />
          <span className="flex-1 text-left">Pesquisar…</span>
          <kbd className="hidden sm:inline text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted">Ctrl K</kbd>
        </button>
      </div>
      <nav className="flex-1 px-2 py-1 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={l.testid}
            onClick={handleClick}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all border-l-[3px] ` +
              (isActive
                ? "nav-active font-medium"
                : "border-transparent text-foreground/70 hover:text-foreground hover:bg-muted/60")
            }
          >
            <l.icon size={17} />
            <span className="flex-1">{l.label}</span>
            {l.to === "/notifications" && unread > 0 && (
              <span className="text-[10px] font-semibold rounded-full bg-brand-red text-white px-1.5 py-0.5 min-w-[18px] text-center" data-testid="nav-notifications-badge">
                {unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t space-y-2">
        <div className="flex items-center justify-between px-2">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={toggle} data-testid="theme-toggle-btn">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            <span className="ml-1.5 text-xs">{theme === "dark" ? "Claro" : "Escuro"}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="text-brand-red hover:text-brand-red hover:bg-brand-red/10">
            <LogOut size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ onSearchOpen }) {
  return (
    <aside className="hidden lg:flex w-60 shrink-0 border-r bg-card flex-col h-screen sticky top-0" data-testid="sidebar">
      <SidebarContent onSearchOpen={onSearchOpen} />
    </aside>
  );
}
